import React, { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";

import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";

/**
 * Backend endpoint (Client Intel feedbacks)
 */
const FEEDBACK_API_URL = `${process.env.REACT_APP_BASE_URL}/pfg-ci-feedbacks`;

/**
 * UI Section -> Agent mapping (as provided)
 */
const UI_SECTION_TO_AGENT = {
  recent_news: "market_news_intelligence_agent",
  consultant_intelligence: "consultant_intelligence_agent",
  investment_commentary: "consolidated_context_agent",
  churn_risk_overview: "churn_risk_assessment_agent",
  ai_recommended_opportunities: "opportunity_discovery_agent",
  client_priorty: "account_strategy_planning_agent", // NOTE: spelling kept as provided
  talking_points: "meeting_prep_agent",
  ls_plans: "meeting_prep_agent",
  highlights: "meeting_prep_agent",
};

/**
 * AiSectionFeedback
 * - 👍 opens dialog (feedback optional). Submit => has_user_satisfaction: 1
 * - 👎 opens dialog (feedback optional). Submit => has_user_satisfaction: 0
 *
 * Required for backend submit:
 * - requestId: api request id used to fetch the displayed insights
 * - uiSectionKey: one of the mapping keys (recent_news, consultant_intelligence, etc.)
 * - userEmail: CI user email
 */
const AiSectionFeedback = ({
  sectionName,
  entityId,

  requestId,
  uiSectionKey,
  userEmail,

  size = "small",
  variant = "light", // "light" | "dark"
}) => {
  const isDark = variant === "dark";

  const [selection, setSelection] = useState(null); // "up" | "down" | null (for UI highlight only)
  const [openDialog, setOpenDialog] = useState(false);
  const [pendingSentiment, setPendingSentiment] = useState(null); // "up" | "down"
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const styles = useMemo(() => {
    const base = {
      borderRadius: "999px",
      border: "1px solid",
      borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(15,23,42,0.12)",
      bgcolor: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,51,102,0.03)",
    };

    const activeUp = {
      bgcolor: isDark ? "rgba(34,197,94,0.18)" : "rgba(34,197,94,0.12)",
      borderColor: isDark ? "rgba(34,197,94,0.55)" : "rgba(34,197,94,0.35)",
    };

    const activeDown = {
      bgcolor: isDark ? "rgba(239,68,68,0.18)" : "rgba(239,68,68,0.10)",
      borderColor: isDark ? "rgba(239,68,68,0.55)" : "rgba(239,68,68,0.30)",
    };

    return { base, activeUp, activeDown };
  }, [isDark]);

  const showSnack = (severity, msg) => {
    setSnack({ open: true, severity, msg });
  };

  const openFeedbackDialog = (sentiment) => {
    setPendingSentiment(sentiment); // "up" | "down"
    setOpenDialog(true);
  };

  const handleThumbUp = () => {
    setSelection("up");
    openFeedbackDialog("up");
  };

  const handleThumbDown = () => {
    setSelection("down");
    openFeedbackDialog("down");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPendingSentiment(null);
    setFeedbackText("");
  };

  const submitFeedback = async () => {
    const trimmed = (feedbackText || "").trim();

    // Feedback optional; if empty => no action
    if (!trimmed) {
      handleCloseDialog();
      return;
    }

    // Validate required backend fields
    if (!requestId) {
      showSnack("error", "Missing request_id. Unable to submit feedback.");
      return;
    }
    if (!uiSectionKey) {
      showSnack("error", "Missing uiSectionKey. Unable to submit feedback.");
      return;
    }
    if (!userEmail) {
      showSnack("error", "Missing user email. Unable to submit feedback.");
      return;
    }
    if (!pendingSentiment) {
      showSnack("error", "Missing feedback type. Please try again.");
      return;
    }

    const agentName = UI_SECTION_TO_AGENT[uiSectionKey] || "unknown_agent";

    const hasUserSatisfaction = pendingSentiment === "up" ? 1 : 0;

    setIsSubmitting(true);

    try {
      const payload = {
        request_id: requestId,
        agent_name: agentName,
        has_user_satisfaction: hasUserSatisfaction,
        user_email: userEmail,
        feedback_comments: trimmed,
      };

      const res = await fetch(FEEDBACK_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let msg = `Feedback submit failed (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.message || data?.error || msg;
        } catch (e) {
          // ignore JSON parse errors
        }
        throw new Error(msg);
      }

      showSnack("success", "Feedback submitted");

      // reset so user can submit again later
      setSelection(null);
      handleCloseDialog();
    } catch (e) {
      showSnack(
        "error",
        e?.message || "Something went wrong while submitting feedback"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const dialogTitle =
    pendingSentiment === "up"
      ? "What worked well? (optional)"
      : "What should be improved? (optional)";

  return (
    <>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        <span>
          <IconButton
            size={size}
            onClick={handleThumbUp}
            sx={{
              ...styles.base,
              ...(selection === "up" ? styles.activeUp : {}),
              p: 0.65,
            }}
          >
            {selection === "up" ? (
              <ThumbUpAltIcon
                fontSize="small"
                sx={{ color: isDark ? "#ffffff" : "#16a34a" }}
              />
            ) : (
              <ThumbUpAltOutlinedIcon
                fontSize="small"
                sx={{ color: isDark ? "#ffffff" : "#16a34a" }}
              />
            )}
          </IconButton>
        </span>

        <span>
          <IconButton
            size={size}
            onClick={handleThumbDown}
            sx={{
              ...styles.base,
              ...(selection === "down" ? styles.activeDown : {}),
              p: 0.65,
            }}
          >
            {selection === "down" ? (
              <ThumbDownAltIcon
                fontSize="small"
                sx={{ color: isDark ? "#ffffff" : "#ef4444" }}
              />
            ) : (
              <ThumbDownAltOutlinedIcon
                fontSize="small"
                sx={{ color: isDark ? "#ffffff" : "#ef4444" }}
              />
            )}
          </IconButton>
        </span>
      </Box>

      {/* Feedback dialog (shared for 👍 and 👎) */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {dialogTitle}
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mb: 1.2 }}>
            <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>
              {sectionName ? `Section: ${sectionName}` : ""}
              {entityId ? ` • Client: ${entityId}` : ""}
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={4}
            placeholder="Type your feedback..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            size="small"
            sx={{ textTransform: "none", borderRadius: "999px", px: 2.5 }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            onClick={submitFeedback}
            variant="contained"
            size="small"
            sx={{ textTransform: "none", borderRadius: "999px", px: 2.5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                <CircularProgress size={16} sx={{ color: "white" }} />
                Submitting
              </Box>
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2600}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AiSectionFeedback;
